import React from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, X } from 'lucide-react';

interface ConfirmDialogProps {
	isOpen: boolean;
	title: string;
	message: string;
	onConfirm: () => void;
	onCancel: () => void;
	confirmText?: string;
	cancelText?: string;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
	isOpen,
	title,
	message,
	onConfirm,
	onCancel,
	confirmText = 'Confirm',
	cancelText = 'Cancel',
}) => {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
			{/* Backdrop - clicking it closes the dialog */}
			<div className="absolute inset-0" onClick={onCancel} />

			{/* Dialog */}
			<div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 pt-4">
				{/* Close button */}
				<button onClick={onCancel} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
					<X className="h-5 w-5" />
				</button>

				{/* Icon */}
				<div className="flex items-center gap-3 mb-3">
					<div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
						<AlertCircle className="h-6 w-6 text-orange-600" />
					</div>
					<h3 className="text-lg font-semibold text-gray-900">{title}</h3>
				</div>

				{/* Message */}
				<p className="text-sm text-gray-600 mb-5">{message}</p>

				{/* Actions */}
				<div className="flex gap-3 justify-end">
					<button
						onClick={onCancel}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors">
						{cancelText}
					</button>
					<button
						onClick={() => {
							onConfirm();
							onCancel();
						}}
						className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-orange-600 rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors">
						{confirmText}
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};
